import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';
import {
  toAussieStandardDateFormat,
  toDDMMYYYY,
} from 'scenes/common/helpers/datetime';

import {
  MotorFullPageProps,
  MotorPolicySubSection,
  MotorQuoteSubSection,
  MotorFunnelState,
  MotorSection,
} from 'scenes/motor/types';
import {
  sameOvernightAndPostalAddress,
  getBusinessContactName,
  getBusinessNameAndAbn,
  getContactEmailAddress,
  getContactPhoneNumber,
  getOvernightParkingAddress,
  getPolicyStartDate,
  getPostalAddress,
  getPrimaryPolicyHolderPersonalContact,
  getSelectedPolicyHolders,
  hasBusinessRegisteredForGst,
  isPolicyIssuedToBusiness,
  getFlybuysNumber,
  getLockedBoxAddress,
  isPostalAddressLockedBagGpo,
} from 'scenes/motor/selectors';
import SummaryCard from 'components/SummaryCard';
import { toDisplayAddress } from 'scenes/common/helpers/address';

const findPostalAddress = (funnelState: MotorFunnelState) => {
  if (sameOvernightAndPostalAddress(funnelState)) {
    return getOvernightParkingAddress(funnelState);
  }
  if (isPostalAddressLockedBagGpo(funnelState)) {
    return getLockedBoxAddress(funnelState);
  }
  return getPostalAddress(funnelState);
};

const BusinessSummary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditPolicyDetails }) => (
  <>
    <SummaryCard
      heading="Business details"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.BusinessNameAndAbn
        )
      }
    >
      <Text>
        {getBusinessNameAndAbn(funnelState).businessName}
        <br />
        ABN: {getBusinessNameAndAbn(funnelState).abn}
      </Text>
    </SummaryCard>
    <SummaryCard
      heading="Business registered for GST?"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.BusinessRegisteredForGst
        )
      }
    >
      <Text>{hasBusinessRegisteredForGst(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Main business contact's name"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.BusinessContact
        )
      }
    >
      <Text>
        {`${getBusinessContactName(funnelState).firstName} ${
          getBusinessContactName(funnelState).lastName
        }`}
      </Text>
    </SummaryCard>
    <SummaryCard
      heading="Additional business contact(s)"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.AddPolicyHolder
        )
      }
    >
      <Text>
        {getSelectedPolicyHolders(funnelState).length > 0
          ? getSelectedPolicyHolders(funnelState).map((ph) => (
              <React.Fragment key={`${ph.firstName}-${ph.lastName}`}>
                {`${ph.firstName} ${ph.lastName}`}
                <br />
              </React.Fragment>
            ))
          : 'None'}
      </Text>
    </SummaryCard>
    <SummaryCard
      heading="Main business contact's phone no."
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.ContactPhoneNumber
        )
      }
    >
      <Text>{getContactPhoneNumber(funnelState)}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Main business contact's email address"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.ContactEmailAddress
        )
      }
    >
      <Text>{getContactEmailAddress(funnelState)}</Text>
    </SummaryCard>

    <SummaryCard
      heading="Business postal address"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.PostalAddress
        )
      }
    >
      <Text>{toDisplayAddress(findPostalAddress(funnelState))}</Text>
    </SummaryCard>
  </>
);

const PersonSummary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditPolicyDetails }) => {
  const personIssued = getPrimaryPolicyHolderPersonalContact(funnelState);

  return (
    <>
      <SummaryCard
        heading="Policy holder(s)"
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.PolicyDetails,
            MotorPolicySubSection.PersonalPolicyContact
          )
        }
      >
        <Text>
          {getSelectedPolicyHolders(funnelState).map((ph) => (
            <React.Fragment key={`${ph.firstName}-${ph.lastName}`}>
              {`${ph.firstName} ${ph.lastName}`} -{' '}
              {toAussieStandardDateFormat(ph.dob)}
              <br />
            </React.Fragment>
          ))}
        </Text>
      </SummaryCard>
      <SummaryCard
        heading="Main policy holder"
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.PolicyDetails,
            MotorPolicySubSection.PersonalPolicyContact
          )
        }
      >
        <Text>{`${personIssued.personalContactFirstName} ${personIssued.personalContactLastName}`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Main policy holder's phone no."
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.PolicyDetails,
            MotorPolicySubSection.ContactPhoneNumber
          )
        }
      >
        <Text>{getContactPhoneNumber(funnelState)}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Main policy holder's email address"
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.PolicyDetails,
            MotorPolicySubSection.ContactEmailAddress
          )
        }
      >
        <Text>{getContactEmailAddress(funnelState)}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Main policy holder's postal address"
        onEditClick={() =>
          onEditPolicyDetails(
            MotorSection.PolicyDetails,
            MotorPolicySubSection.PostalAddress
          )
        }
      >
        <Text>{toDisplayAddress(findPostalAddress(funnelState))}</Text>
      </SummaryCard>
    </>
  );
};

export const PolicyDetailsSummary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditPolicyDetails }) => (
  <>
    <SummaryCard
      heading="Policy start date"
      onEditClick={() =>
        onEditQuoteDetails(
          MotorSection.PolicyDetails,
          MotorQuoteSubSection.PolicyStartDate
        )
      }
    >
      <Text>{`${toAussieStandardDateFormat(
        toDDMMYYYY(getPolicyStartDate(funnelState))
      )}`}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Policy issued to a person or business?"
      onEditClick={() =>
        onEditPolicyDetails(
          MotorSection.PolicyDetails,
          MotorPolicySubSection.PolicyIssuedTo
        )
      }
    >
      {isPolicyIssuedToBusiness(funnelState) ? (
        <Text>Business</Text>
      ) : (
        <Text>Person</Text>
      )}
    </SummaryCard>
    {!isPolicyIssuedToBusiness(funnelState) ? (
      <PersonSummary
        funnelState={funnelState}
        onEditPolicyDetails={onEditPolicyDetails}
      />
    ) : (
      <BusinessSummary
        funnelState={funnelState}
        onEditPolicyDetails={onEditPolicyDetails}
      />
    )}
    <SummaryCard
      heading="Flybuys membership no."
      onEditClick={() =>
        onEditQuoteDetails(
          MotorSection.PolicyDetails,
          MotorQuoteSubSection.FlybuysNumber
        )
      }
    >
      <Text>
        {getFlybuysNumber(funnelState)
          ? `6008 XXXX XXXX ${getFlybuysNumber(funnelState).substring(
              getFlybuysNumber(funnelState).length - 4
            )}`
          : 'None'}
      </Text>
    </SummaryCard>
  </>
);

export default PolicyDetailsSummary;
